import apiClient, { publicApiClient } from "./apiClient";
import { AdminInvitation, AdminUser, InviteAdminPayload, Permission, Role, RoleCreationPayload, RolePermissions } from "../types/admin";
import { readPage } from "../utils/paginatedResult";
import { normalizeEmailAddress } from "../utils/emailValidation";

export async function getAdmins(page = 1, signal?: AbortSignal) {
  return readPage<AdminUser>((await apiClient.get("rbac/admins", { params: { page, limit: 20 }, signal })).data);
}
export async function getRoles(signal?: AbortSignal): Promise<Role[]> {
  const { data } = await apiClient.get<Role[]>("auth/roles", { signal });
  if (!Array.isArray(data)) throw new Error("Unable to load roles.");
  return data;
}
export async function createRole(body: RoleCreationPayload): Promise<{ role: Role }> {
  return (await apiClient.post("auth/create-role", body)).data;
}
export async function getPermissions(signal?: AbortSignal): Promise<Permission[]> {
  const { data } = await apiClient.get<Permission[]>("rbac/permissions", { signal });
  if (!Array.isArray(data)) throw new Error("Unable to load the permission catalog.");
  return data;
}
export async function getRolePermissions(id: string, signal?: AbortSignal): Promise<RolePermissions> {
  return (await apiClient.get<RolePermissions>(`rbac/roles/${encodeURIComponent(id)}/permissions`, { signal })).data;
}
export async function replaceRolePermissions(id: string, permissionKeys: string[]) {
  return (await apiClient.put(`rbac/roles/${encodeURIComponent(id)}/permissions`, { permissionKeys })).data;
}
export async function updateRole(id: string, body: { name?: string; description?: string | null }) {
  return (await apiClient.patch(`rbac/roles/${encodeURIComponent(id)}`, body)).data;
}
export async function deleteRole(id: string) { await apiClient.delete(`rbac/roles/${encodeURIComponent(id)}`); }
export async function assignAdminRole(adminId: string, roleId: string) {
  await apiClient.patch(`rbac/admins/${encodeURIComponent(adminId)}/role`, { roleId });
}
export async function inviteAdmin(body: InviteAdminPayload) {
  return (await apiClient.post<{ message: string; invitationUrl?: string }>("auth/invite-admin", { ...body, email: normalizeEmailAddress(body.email) })).data;
}
export async function getInvitations(params: { page: number; search?: string; startDate?: string; endDate?: string }, signal?: AbortSignal) {
  const query = Object.fromEntries(Object.entries({ ...params, limit: 20 }).filter(([, value]) => value !== ""));
  return readPage<AdminInvitation>((await apiClient.get("auth/admin-invitations", { params: query, signal })).data);
}
export async function resendInvitation(adminId: string) {
  return (await apiClient.post<{ message: string }>(`auth/admin-invitations/${encodeURIComponent(adminId)}/resend`)).data;
}
export async function acceptInvitation(body: { requestId: string; token: string; password: string; confirmPassword: string }) {
  await publicApiClient.post("auth/accept-invitation", body);
}

export async function createInvitationLink(adminId: string): Promise<{ invitationUrl: string; expiresAt: string }> {
  return (await apiClient.post(`auth/admin-invitations/${encodeURIComponent(adminId)}/link`)).data;
}
